//@flow
import React from 'react';
import { themr } from 'react-css-themr';
import { Checkbox, CheckboxTypes } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import * as Immutable from 'immutable';

import HomebaseActions from 'common/redux/actions/homebaseMaster';
import ToastActions from 'common/redux/actions/toast';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import TeamList from 'common/components/core/common/TeamList';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import HomebaseSelectors from 'common/redux/selectors/homebaseMaster';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { fetchHomebasesByClientId } from 'common/redux/actions/homebase';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { withRouter } from 'common/components/core/router';
import SingleHomebaseEditContainer from 'common/entity/homebaseMaster/SingleHomebaseEditContainer';

import HomebaseMasterImportContainer from 'common/entity/homebaseMaster/HomebaseMasterImportContainer';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import { ConnectedTeamSelectorFilter } from 'common/entity/components/TeamSelectorFilter';
import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';

import SlotFilter from 'common/entity/components/SlotFilter';
import TimeSlotsSelectorModalContainer from 'common/entity/components/multiDaySlot/TimeSlotsSelectorModalContainer';
import LocationSlotsSummary from 'common/entity/components/LocationSlotSummary';

import { ErrorIcon } from 'common/components/core/icon';

import { Modal } from 'common/entity/generic/SingleEntityEditView';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import { dashboardColors } from 'common/enums/ColorEnum';

import theme from 'common/entity/BulkEditContainer.scss';

import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

import PromiseUtils, { PromiseStatus } from 'common/utils/PromiseUtils';
import VisitUtils from 'common/utils/VisitUtils';

import { Loader } from 'common/components/core/loader';
import { Button, ButtonTypes } from 'common/components/core/button';
import BulkEditConfirmationDialog from 'common/entity/generic/BulkEditConfirmationDialog';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { RoleOperationEnum } from 'common/modules/rbac';

import {
    HomebaseType,
    MasterHomebase,
    CrossDocksType
} from 'common/models/internal/MasterHomebase';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import type { RouterHistory } from 'react-router-dom';
import type { Location } from 'common/components/core/router';
import type { Team } from 'common/models/internal/Team';
import type { Map } from 'immutable';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { ToastType } from 'common/components/core/toast/Toast';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';

import { MultiDaySlot } from 'common/models/internal/MultiDaySlot';

import homebaseIds from 'common/tests/masterManagement/homebaseMaster.json';
import EnableHomebaseModal from 'common/entity/homebaseMaster/EnableHomebaseModal';
import {
    ENTITY_TABLE_ID,
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID
} from 'common/constants/entity/homebaseMaster';
import DataUtils from 'common/utils/DataUtils';

const selectTeamId = ({ teamId }) => teamId;

const COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick, theme }
                }
            } = props;
            return (
                <Checkbox
                    checkboxType={CheckboxTypes.Solid}
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    theme={theme}
                    className={theme.selectionCell}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'homebaseMasterTable.id',
        accessor: 'id',
        frozen: true,
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'homebaseMasterTable.code',
        id: 'code',
        accessor: 'code',
        frozen: true,
        minWidth: 100,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'homebaseMasterTable.type',
        id: 'homebaseType',
        accessor: 'type',
        Cell: props => {
            const {
                value,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const cellText = t(`masterHomebaseProperties.types.${value}`);
            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        },
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('type')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = HomebaseType.getOptions({
                translationKey: 'masterHomebaseProperties.types'
            });
            return options;
        },
        property: 'type',
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.crossDockType',
        id: 'crossDockInfo',
        accessor: 'crossDockInfo',
        sortable: false,
        exportId: 'crossDockInfo.typesSupported',
        Cell: props => {
            const {
                value,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const { typesSupported } = value || {};
            if (DataUtils.isEmpty(typesSupported)) {
                return '-';
            }
            return (
                <div className={theme.cellText}>
                    {typesSupported
                        .map(crossDockType =>
                            t(
                                `masterHomebaseProperties.crossDockTypes.${crossDockType}`
                            )
                        )
                        .join(', ')}
                </div>
            );
        },
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('crossDockInfo.typesSupported')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = CrossDocksType.getOptions({
                translationKey: 'masterHomebaseProperties.crossDockTypes'
            });
            return options;
        },
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.crossDockLowerThresh',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('entities:task_plural')
        }),
        id: 'minTask',
        accessor: 'crossDockInfo.taskLimit.lowerThresh',
        fieldId: 'crossDockInfo.taskLimit.lowerThresh',
        property: 'crossDockInfo.taskLimit.lowerThresh',
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.crossDockHigherThresh',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('entities:task_plural')
        }),
        id: 'maxTask',
        accessor: 'crossDockInfo.taskLimit.higherThresh',
        fieldId: 'crossDockInfo.taskLimit.higherThresh',
        property: 'crossDockInfo.taskLimit.higherThresh',
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.name',
        accessor: 'name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'homebaseMasterTable.teamName',
        id: 'teams.name',
        exportId: 'teams.name',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: ({ clientId, filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            const teamValueFilter = values.map(team => ({
                clientId,
                teamId: team
            }));

            return new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValues(teamValueFilter)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'homebaseMasterTable.teamId',
        id: 'teams.id',
        exportId: 'teams.id',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: ({ clientId, filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            const teamValueFilter = values.map(team => ({
                clientId,
                teamId: team
            }));

            return new FilterBuilder()
                .withName('teams')
                .withOperation(FilterOperation.Contains)
                .withValues(teamValueFilter)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'homebaseMasterTable.address.placeName',
        accessor: 'address.placeName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'homebaseMasterTable.address.address',
        accessor: 'address.formattedAddress',
        minWidth: 300,
        infixSearchable: true
    },
    {
        headerTextId: 'homebaseMasterTable.address.locality',
        accessor: 'address.localityName',
        minWidth: 150
    },
    {
        headerTextId: 'homebaseMasterTable.address.pincode',
        accessor: 'address.pincode',
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.address.city',
        accessor: 'address.city',
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.address.state',
        accessor: 'address.state',
        minWidth: 100
    },
    {
        headerTextId: 'homebaseMasterTable.address.country',
        accessor: 'address.countryCode',
        minWidth: 50
    },
    {
        headerTextId: 'homebaseMasterTable.latitude',
        exportId: 'latitude',
        accessor: 'latLng.lat',
        sortable: false,
        filterable: false,
        minWidth: 150
    },
    {
        headerTextId: 'homebaseMasterTable.longitude',
        exportId: 'longitude',
        accessor: 'latLng.lng',
        sortable: false,
        filterable: false,
        minWidth: 150
    },
    {
        headerTextId: 'homebaseMasterTable.slots',
        id: 'slots',
        fieldId: 'timeSlots',
        property: 'timeSlots',
        sortable: false,
        minWidth: 150,
        FilterComponent: SlotFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values, allowEmptyOrNull }
            } = filteredColumn;
            const slots = values.map(slot => ({
                dayOfWeek: slot
            }));
            return new FilterBuilder()
                .withName('timeSlots')
                .withOperation(FilterOperation.Contains)
                .withValues(slots)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        accessor: (data: MasterHomebase) => {
            const { timeSlots } = data.sortTimeSlots();
            const DaysOfWeekActive = [];
            timeSlots.forEach(timeSlot => {
                if (timeSlot.slots.size) {
                    DaysOfWeekActive.push(timeSlot.getShortCode());
                }
            });
            return DaysOfWeekActive.join(' ');
        },
        Cell: props => {
            const { original: homebase, row } = props;
            const { t } = props.column.getHeaderProps();
            const { timeSlots } = homebase.initializeWeekSlots();
            return (
                <LocationSlotsSummary
                    timeSlots={timeSlots}
                    title={t('workingHours')}
                    testId={`${homebaseIds.table.locationSlots}-${row._index}`}
                />
            );
        }
    },
    {
        headerTextId: 'homebaseMasterTable.tat',
        id: 'transactionDuration',
        property: 'transactionDuration',
        filterable: false,
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'transactionDuration',
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'homebaseMasterTable.contactId',
        accessor: 'contactId.contactId',
        fieldId: 'contact',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'homebaseMasterTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: homebase,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                homebase?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'homebaseMasterTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'homebaseMasterTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: homebase,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                homebase?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'homebaseMasterTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

const BulkEditErrorsModal = ({ t, theme, errors, onClose }) => {
    return (
        <div className={theme.bulkEditErrorsModal}>
            <span className={theme.title}>
                {t('bulkEdit.updatesFailed', { count: errors.length })}
            </span>
            <Button
                buttonType={ButtonTypes.Icon}
                circle
                icon="cancel"
                className={classnames(theme.button, theme.closeIcon)}
                theme={theme}
                onClick={onClose}
            />
            <div className={theme.errorsList}>
                {errors.map((error, index) => {
                    return (
                        <span key={index} className={theme.error}>
                            <ErrorIcon visible theme={theme} /> {error}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type HomebasesDataType = {
    homebases: Array<MasterHomebase>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    teams: Map<string, Team>,
    fetchEntities: ({ clientId: string }, Object, Object) => void,
    disableEntity: ({ clientId: string, homebaseId: string }) => Promise<*>,
    clearData: () => void,
    selectedRows: Array<string>,
    data: ?HomebasesDataType,
    isAddHomebaseActive: boolean,
    entityIdToEdit: ?string,
    isBulkEditHomebaseActive: boolean,
    isLoading: boolean,
    location: Location,
    history: RouterHistory,
    theme: Object,
    t: (string, ?Object) => string,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    updateHomebase: (
        {
            clientId: string,
            homebaseId: string
        },
        MasterHomebase,
        MasterHomebase,
        options?: Object
    ) => Promise<*>,
    showSuccessToast: ($Shape<ToastType>) => void,
    showErrorToast: ($Shape<ToastType>) => void,
    saveTableSettings: (string, Array<Object>) => void,
    toggleRowSelection: string => void,
    isGlobalPersonnel: boolean,
    fetchHomebasesByClientId: (clientId: string) => void
};

type State = {
    showSlotSelector: boolean,
    showBulkEditProgress: boolean,
    showBulkEditErrors: boolean,
    bulkEditErrors: Array<string>,
    needsBulkEditConfirmation?: boolean,
    bulkEditAcceptCallback?: ?(any) => void,
    prevFetchEntitiesParams: Object
};

const HomebaseMaster = entityMasterFactory({
    EntityEditViewContainer: SingleHomebaseEditContainer,
    EntityImportContainer: HomebaseMasterImportContainer,
    EntityExportContainer: MasterExportContainer,
    COLUMNS,
    options: {
        entityTableId: 'HomebaseMaster',
        operations: {
            edit: RoleOperationEnum.UPDATE_HOMEBASES,
            add: RoleOperationEnum.UPDATE_HOMEBASES,
            export: RoleOperationEnum.EXPORT_HOMEBASES
        }
    },
    EnableEntityView: EnableHomebaseModal
});

class HomebaseMasterContainer extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            showSlotSelector: false,
            showBulkEditProgress: false,
            showBulkEditErrors: false,
            bulkEditErrors: [],
            prevFetchEntitiesParams: {}
        };
    }

    componentWillUnmount() {
        //Update the homebases list when leaving homebase master
        const { clientId, fetchHomebasesByClientId } = this.props;
        fetchHomebasesByClientId(clientId);
    }

    getExtraColumnProperties = () => {
        const { t } = this.props;
        return [
            {
                id: 'slots',
                visible: true,
                menu: [
                    {
                        id: 'change-slots',
                        label: t('homebaseMasterTable.changeSlots'),
                        onClick: this.toggleSlotSelectorVisiblity,
                        isDisabled: this.isBulkSlotsChangeDisabled
                    }
                ]
            },
            ...this.getCustomColumnProperties()
        ];
    };

    getCustomColumnProperties() {
        return CustomColumnHelpers.makeCustomColumnProperties(
            this.props.entitySettings,
            'homebaseSettings',
            this.onCustomColumnOptionClick,
            this.isCustomColumnOptionDisabled
        );
    }

    isCustomColumnOptionDisabled = () => {
        return !this.props.selectedRows.length;
    };

    onCustomColumnOptionClick = (
        customColumnName: string,
        optionValue: string
    ) => {
        this.setState({
            needsBulkEditConfirmation: true,
            bulkEditAcceptCallback: () => {
                this.showBulkEditProgress();
                this.applyCustomColumnBulkEdit(customColumnName, optionValue);
            }
        });
    };

    applyCustomColumnBulkEdit = (
        customColumnName: string,
        optionValue: string
    ) => {
        const selectedHomebases = this.getAllSelectedHomebases();
        const promisesToProcess = {};
        selectedHomebases.forEach(homebase => {
            const { customProperties = Immutable.Map() } = homebase;
            const updatedHomebase = homebase.setCustomProperties(
                customProperties.set(customColumnName, optionValue)
            );
            promisesToProcess[homebase.id] = this.props.updateHomebase(
                {
                    clientId: updatedHomebase.clientId,
                    homebaseId: updatedHomebase.id
                },
                homebase,
                updatedHomebase,
                { dontInjectError: true, suppressToasts: true }
            );
        });
        this.processBulkEditPromises(promisesToProcess, {
            onRetry: this.applyCustomColumnBulkEdit.bind(
                this,
                customColumnName,
                optionValue
            )
        });
    };

    handleBulkEditApproval = () => {
        const { bulkEditAcceptCallback } = this.state;
        bulkEditAcceptCallback && bulkEditAcceptCallback();
        this.handleBulkEditDismiss();
    };

    handleBulkEditDismiss = () => {
        this.setState({
            needsBulkEditConfirmation: false,
            bulkEditAcceptCallback: null
        });
    };

    showBulkEditProgress = () => {
        this.setState({
            showBulkEditProgress: true
        });
    };

    hideBulkEditProgress = () => {
        this.setState({ showBulkEditProgress: false });
    };

    showBulkEditErrors = () => {
        this.setState({
            showBulkEditErrors: true
        });
    };

    hideBulkEditErrors = () => {
        this.setState({
            showBulkEditErrors: false
        });
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const { original } = rowInfo;
        const { id } = original;
        const {
            teams,
            selectedRows,
            toggleRowSelection,
            t,
            theme,
            isGlobalPersonnel
        } = this.props;
        const isChecked = selectedRows.includes(id);
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            isChecked,
            className: classnames({ [theme.selectedRow]: isChecked }),
            theme,
            t,
            teams,
            isGlobalPersonnel
        };
    };

    toggleSlotSelectorVisiblity = () => {
        const { selectedRows } = this.props;
        const { showSlotSelector } = this.state;
        if (!showSlotSelector) {
            if (!selectedRows.length) {
                return;
            }
            this.setState({ showSlotSelector: true });
            return;
        }
        this.setState({ showSlotSelector: false });
    };

    getAllSelectedHomebases = () => {
        const { data, selectedRows } = this.props;
        if (!data) {
            throw new Error("can't find homebases data");
        }
        return data.entities.filter(homebase => {
            return selectedRows.indexOf(homebase.id) > -1;
        });
    };

    isBulkSlotsChangeDisabled = () => {
        return this.props.selectedRows.length === 0;
    };

    handleSlotsChange = (slots: Array<MultiDaySlot>) => {
        this.showBulkEditProgress();
        this.setSlotsForHomebases(slots, this.props.selectedRows);
    };

    setSlotsForHomebases = (slots: Array<MultiDaySlot>, ids: Array<string>) => {
        const { data } = this.props;
        if (!data) {
            throw new Error("can't find homebasesData");
        }
        const selectedHomebases = data.entities.filter(homebase => {
            return ids.indexOf(homebase.id) > -1;
        });
        const promisesToProcess = {};
        selectedHomebases.forEach(homebase => {
            const updatedHomebase = homebase.setTimeSlots(
                Immutable.List(slots)
            );
            promisesToProcess[homebase.id] = this.props.updateHomebase(
                {
                    clientId: updatedHomebase.clientId,
                    homebaseId: updatedHomebase.id
                },
                homebase,
                updatedHomebase,
                { dontInjectError: true, suppressToasts: true }
            );
        });
        this.processBulkEditPromises(promisesToProcess, {
            onRetry: this.setSlotsForHomebases.bind(this, slots)
        });
    };

    processBulkEditPromises = (
        promisesToProcess: { [key: string]: Promise<*> },
        options: {
            onRetry: (Array<string>) => void
        }
    ) => {
        const { showSuccessToast, showErrorToast, t } = this.props;
        const { onRetry } = options;
        this.setState({
            bulkEditErrors: [],
            showBulkEditErrors: false
        });
        PromiseUtils.processAll(promisesToProcess).then(results => {
            const rejectedPromiseIds: Array<string> = [];
            results.forEach(result => {
                if (result.status === PromiseStatus.REJECTED && result.id) {
                    rejectedPromiseIds.push(result.id);
                    this.setState(state => ({
                        bulkEditErrors: [
                            ...state.bulkEditErrors,
                            result.error.message
                        ]
                    }));
                }
            });
            if (rejectedPromiseIds.length !== results.length) {
                showSuccessToast({
                    message: t('homebaseMasterTable.bulkEditSuccessToast', {
                        count: results.length - rejectedPromiseIds.length
                    }),
                    timeout: 5000
                });
            }
            if (rejectedPromiseIds.length === 0) {
                // Stop Loading
                this.hideBulkEditProgress();
                return;
            }

            const errorContent = (
                <div className={theme.errorToastContentWrap}>
                    <span className={theme.message}>
                        {t('bulkEdit.updatesFailed', {
                            count: rejectedPromiseIds.length
                        })}
                    </span>
                    <span
                        className={theme.viewErrors}
                        onClick={this.showBulkEditErrors}
                    >
                        {t('bulkEdit.viewErrors')}
                    </span>
                </div>
            );

            const actions = ({ hideToast }) => {
                const tryAgain = () => {
                    onRetry(rejectedPromiseIds);
                    hideToast();
                };

                const cancel = () => {
                    hideToast();
                    this.hideBulkEditProgress();
                };
                return (
                    <div className={theme.errorToastButtons}>
                        <Button
                            flat
                            semibold
                            uppercase
                            label={t('bulkEdit.tryAgain')}
                            color={dashboardColors.salmonTwo.name}
                            theme={theme}
                            buttonType={ButtonTypes.Text}
                            onClick={tryAgain} //eslint-disable-line react/jsx-no-bind
                        />
                        <Button
                            buttonType={ButtonTypes.Icon}
                            circle
                            icon="cancel"
                            className={classnames(
                                theme.button,
                                theme.closeIcon
                            )}
                            theme={theme}
                            onClick={cancel} //eslint-disable-line react/jsx-no-bind
                        />
                    </div>
                );
            };
            showErrorToast({
                customContent: errorContent,
                customAction: actions,
                timeout: 0
            });
        });
    };

    renderBulkProgress() {
        const { theme, t } = this.props;
        const { bulkEditErrors } = this.state;
        return (
            <div className={theme.bulkUpdatesLoader}>
                {bulkEditErrors.length ? (
                    <div className={theme.failedErrorWrap}>
                        <span>{t('bulkEdit.failed')}</span>
                    </div>
                ) : (
                    <Loader
                        theme={theme}
                        active
                        withText
                        title={t('homebaseMasterTable.updating')}
                    />
                )}
            </div>
        );
    }

    getAdditionalTableProps = () => {
        const { teams } = this.props;
        return {
            teams
        };
    };

    fetchEntities = (params, body, options) => {
        const { fetchEntities } = this.props;
        this.setState({
            prevFetchEntitiesParams: {
                params,
                body,
                options
            }
        });
        fetchEntities(params, body, options);
    };

    render() {
        const { t, selectedRows, clientId, theme } = this.props;
        const {
            showSlotSelector,
            showBulkEditProgress,
            showBulkEditErrors,
            bulkEditErrors,
            needsBulkEditConfirmation
        } = this.state;
        return (
            <div className={theme.wrap}>
                {showBulkEditProgress ? this.renderBulkProgress() : null}
                <HomebaseMaster
                    {...this.props}
                    fetchEntities={this.fetchEntities}
                    emptyStateImageSrc="/img/masterHomebase-empty.svg"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId="homebaseId"
                    entitySettingsName="homebaseSettings"
                    fetchOptions={{ clientId }}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    getExtraColumnProperties={this.getExtraColumnProperties}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    entityCanBeDisabled
                >
                    <Modal theme={theme}>
                        {showBulkEditErrors ? (
                            <BulkEditErrorsModal
                                theme={theme}
                                t={t}
                                errors={bulkEditErrors}
                                onClose={this.hideBulkEditErrors}
                            />
                        ) : null}
                    </Modal>
                    {showSlotSelector ? (
                        <TimeSlotsSelectorModalContainer
                            className={theme.slotSelectorModal}
                            theme={theme}
                            defaultSlots={this.getAllSelectedHomebases()[0].timeSlots.toArray()}
                            onDismiss={this.toggleSlotSelectorVisiblity}
                            onSlotApply={this.handleSlotsChange}
                        />
                    ) : null}
                    {needsBulkEditConfirmation ? (
                        <BulkEditConfirmationDialog
                            selectionCount={selectedRows.length}
                            entityI18nId="entities:masterHomebase"
                            onAccept={this.handleBulkEditApproval}
                            onCancel={this.handleBulkEditDismiss}
                        />
                    ) : (
                        []
                    )}
                </HomebaseMaster>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = HomebaseSelectors.isEntitiesLoading(state, ownProps);
    const data = HomebaseSelectors.getEntities(state, ownProps);
    const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
    const isImportEntityActive = isImportEntityActiveSelector(state, ownProps);
    const isExportEntityActive = isExportEntityActiveSelector(state, ownProps);
    const entityIdToEdit = editEntityIdSelector(state, ownProps);
    const teams = teamsSelector(state, ownProps);
    const entitySettings = getEntitySettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

    return {
        clientId,
        teams,
        isLoading,
        data,
        isAddEntityActive,
        entityIdToEdit,
        entitySettings,
        tableSettings,
        isImportEntityActive,
        isExportEntityActive,
        isGlobalPersonnel
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: HomebaseActions.fetchActiveEntities,
    bulkDisableEntity: HomebaseActions.bulkDisableEntity,
    updateHomebase: HomebaseActions.patchEntity,
    disableEntity: HomebaseActions.disableEntity,
    clearData: HomebaseActions.clearData,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    showSuccessToast: ToastActions.showSuccessToast,
    showErrorToast: ToastActions.showErrorToast,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    fetchHomebasesByClientId
});

const enhance = compose(
    themr(null, theme),
    withRouter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);

const ThemedHomebaseMasterContainer = enhance(HomebaseMasterContainer);

export default ThemedHomebaseMasterContainer;
